import { useCallback, useRef, useState } from 'react';

const DEFAULT_MAX_MS = 5 * 60 * 1000;
export const WAVEFORM_BAR_COUNT = 48;

function computeAmplitude(analyser: AnalyserNode): number {
  const timeData = new Uint8Array(analyser.fftSize);
  analyser.getByteTimeDomainData(timeData);
  let sum = 0;
  for (let i = 0; i < timeData.length; i++) {
    const v = (timeData[i] - 128) / 128;
    sum += v * v;
  }
  const rms = Math.sqrt(sum / timeData.length);
  return Math.min(1, Math.max(0.06, rms * 4.5));
}

export function useVoiceRecorder(maxDurationMs = DEFAULT_MAX_MS) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [waveformHistory, setWaveformHistory] = useState<number[]>(() =>
    Array.from({ length: WAVEFORM_BAR_COUNT }, () => 0.08)
  );

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef(0);
  const pausedAccumRef = useRef(0);
  const pauseStartedRef = useRef<number | null>(null);
  const isPausedRef = useRef(false);
  const isRecordingRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  const cleanupStream = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    analyserRef.current = null;
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    pausedAccumRef.current = 0;
    pauseStartedRef.current = null;
    isPausedRef.current = false;
    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);
    setDurationMs(0);
    setWaveformHistory(Array.from({ length: WAVEFORM_BAR_COUNT }, () => 0.08));
  }, []);

  const tickWaveform = useCallback(() => {
    const analyser = analyserRef.current;
    if (analyser && !isPausedRef.current) {
      const amp = computeAmplitude(analyser);
      setWaveformHistory((prev) => [...prev.slice(-(WAVEFORM_BAR_COUNT - 1)), amp]);
    }
    rafRef.current = requestAnimationFrame(tickWaveform);
  }, []);

  const resolveMimeType = (): string => {
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
    if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
    if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
    if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) return 'audio/ogg;codecs=opus';
    return '';
  };

  const startRecording = useCallback(async () => {
    if (isRecordingRef.current) return;
    isRecordingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      chunksRef.current = [];

      const audioContext = new AudioContext();
      await audioContext.resume().catch(() => {});
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.35;
      source.connect(analyser);
      analyserRef.current = analyser;
      rafRef.current = requestAnimationFrame(tickWaveform);

      const mimeType = resolveMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      startTimeRef.current = Date.now();
      pausedAccumRef.current = 0;
      pauseStartedRef.current = null;
      setDurationMs(0);
      setIsRecording(true);
      setIsPaused(false);
      recorder.start(100);

      timerRef.current = window.setInterval(() => {
        if (isPausedRef.current) return;
        const elapsed = Date.now() - startTimeRef.current - pausedAccumRef.current;
        setDurationMs(elapsed);
        if (elapsed >= maxDurationMs) {
          recorder.stop();
        }
      }, 100);
    } catch (err) {
      isRecordingRef.current = false;
      cleanupStream();
      throw err;
    }
  }, [maxDurationMs, tickWaveform, cleanupStream]);

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.pause();
    isPausedRef.current = true;
    pauseStartedRef.current = Date.now();
    setIsPaused(true);
  }, []);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;
    recorder.resume();
    if (pauseStartedRef.current != null) {
      pausedAccumRef.current += Date.now() - pauseStartedRef.current;
      pauseStartedRef.current = null;
    }
    isPausedRef.current = false;
    setIsPaused(false);
  }, []);

  const stopRecording = useCallback((): Promise<{ blob: Blob; durationMs: number } | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        cleanupStream();
        resolve(null);
        return;
      }

      const elapsed = Date.now() - startTimeRef.current - pausedAccumRef.current;
      recorder.onstop = () => {
        const mime = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mime });
        cleanupStream();
        resolve(blob.size > 0 ? { blob, durationMs: elapsed } : null);
      };
      if (recorder.state === 'paused') {
        recorder.resume();
      }
      recorder.stop();
    });
  }, [cleanupStream]);

  const cancelRecording = useCallback(() => {
    chunksRef.current = [];
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = () => cleanupStream();
      if (recorder.state === 'paused') recorder.resume();
      recorder.stop();
    } else {
      cleanupStream();
    }
  }, [cleanupStream]);

  return {
    isRecording,
    isPaused,
    durationMs,
    waveformHistory,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
  };
}

export function formatRecordingDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

export function voiceFilenameForBlob(blob: Blob): string {
  const type = blob.type.toLowerCase();
  if (type.includes('mp4') || type.includes('aac')) return `voice-${Date.now()}.m4a`;
  if (type.includes('ogg')) return `voice-${Date.now()}.ogg`;
  return `voice-${Date.now()}.webm`;
}

export function voiceMimeForBlob(blob: Blob): string {
  if (blob.type) return blob.type.split(';')[0];
  return 'audio/webm';
}
